import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useWebSocket, type WebSocketMessage } from '../hooks/useWebSocket'
import { api } from '../api/client'
import { useAuth } from '../context/AuthContext'
import { parseServerDate } from '../utils/time'
import MessageBubble from './MessageBubble'
import { renderMentions, type MentionNames } from '../utils/mentions'
import ChatInput, { CHAT_INPUT_MIN_H } from './ChatInput'
import EmojiText from './shared/EmojiText'
// 阈值（距底多少算「在底部」）与群视界/DSH 对话同一份来源；本组件有自己的虚拟列表机器，不套整个 hook
import { BOTTOM_THRESHOLD } from '../hooks/useStickToBottom'
// 「我正在读这个会话」的唯一真相（侧边栏据此不画红数泡，本组件据此把已读落回后端）
import { conversationKey, setFollowingConversation, useReadingKey } from '../hooks/useReadingConversation'
import { markConversationRead } from '../hooks/useChatLists'
import ActivityBar, { type ActivityUser } from './ActivityBar'
import ProfileCard from './ProfileCard'
import { EmptyState, MenuPanel, MenuItem } from './ui'
import { Loader2, AlertTriangle, X, ArrowDown, ArrowUp, Bot, User, MessageSquare, Settings, Gamepad2, Globe } from 'lucide-react'
import { getStateDotColor, CHAT_REFRESH_EVENT } from '../constants'
import { useT } from '../i18n/I18nContext'
import { tryOpenWorldWindow } from '../utils/worldView'
import { isTauri, onKeyboardChange } from '../utils/tauri'
import { scrollToInContainer } from '../utils/scroll'
import { useAttachmentUpload } from '../hooks/useAttachmentUpload'
import { AttachmentChips, DropMask } from './AttachmentChips'

// ── 虚拟列表：消息高度估算（纯函数，窗口化渲染用）──
// 只估不量：渲染完不回写真实高度，滚动位置全靠它 + overscan 兜。
// 已知误差来源：图片/附件（一律按 88px 一个算，实际随原图比例变）、长中文（按 28 字符一行，
// 而中文一行只放得下 14~18 字 → 行数偏低）、Markdown 表格与代码块。
// 所以这是像素近似、不是精确布局：误差由 VIRTUAL_OVERSCAN / OVERSCAN_PX 消化，
// 短对话无感，上千条或图文混排时允许有轻微漂移。要做到精确得渲染后回填真实高度。
const estimateMessageHeight = (msg: any): number => {
  const base = 44                        // 气泡 + 间距基础
  const text = msg.content || ''
  const lines = Math.max(1, Math.ceil(text.length / 28))  // 每行约 28 字符
  const textH = lines * 20
  const attachH = (msg.attachments?.length || 0) * 88     // 附件/图片
  const replyH = msg.reply_to ? 26 : 0                     // 回复引用
  return base + textH + attachH + replyH
}

const VIRTUAL_OVERSCAN = 40   // 窗口外预渲染条数（估算误差缓冲）
const OVERSCAN_PX = 1200      // 视口上下各多渲染的像素

// 撤回窗口（与后端一致：2 分钟内、只能撤自己的）；这里只是别让人点了必然失败
const REVOKE_WINDOW_MS = 2 * 60 * 1000

interface Message {
  id: number
  group_id?: number
  session_id?: string
  sender_type: string
  sender_id: number
  sender_name: string | null
  sender_avatar_url?: string | null
  content: string
  reply_to: number | null
  read_at?: string | null
  attachments?: Array<{file_id?: number, name?: string, size?: number, mime_type?: string, type?: string, invitation_id?: number, group_name?: string, inviter_name?: string, status?: string}> | null
  source_public_id?: string | null
  via?: string | null
  message_type?: string
  sender_state?: string | null
  /** 被撤回了：正文不再显示（后端只回 revoked 标记，不给原文） */
  revoked?: boolean
  created_at: string
}

interface ChatViewProps {
  conversationType: 'group' | 'dm'
  conversationId: number | string
  /** 我在这个群里的角色（owner/admin 才能撤别人的消息；私信没有这个） */
  myRole?: string
  /** 群聊面板容器：世界门要盖住整个面板（含面板顶部标题栏），本组件自己的 absolute 只到消息区 */
  overlayHost?: HTMLElement | null
}

const PAGE_SIZE = 20
// 一次补齐最多拉多少页：离开一天也就几百条；再多交给触底继续拉，别把首屏拖住
const CATCH_UP_MAX_PAGES = 50

// ── 模块级工具函数 ──

/** 检查 WebSocket 消息是否属于当前对话 */
function isMessageForThisConversation(
  data: { group_id?: number; session_id?: string },
  convType: 'group' | 'dm',
  convId: number | string,
): boolean {
  return (convType === 'group' && (data as any).group_id === convId) ||
         (convType === 'dm' && (data as any).session_id === convId)
}

/**
 * 上下哨兵：滚到边界就去加载下一页。
 *
 * 游标走 ref（oldestIdRef / newestIdRef）而不是 messages：observer 只在 hasMore / loadingState /
 * 方向变化时重建，不必每来一条消息就重新订阅一次。
 */
function useSentinel({
  containerRef, sentinelRef, cursorRef, hasMore, direction, loadingState, loadMessages,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  sentinelRef: React.RefObject<HTMLDivElement | null>
  cursorRef: React.RefObject<number | null>
  hasMore: boolean
  direction: 'older' | 'newer'
  loadingState: 'initial' | 'older' | 'newer' | null
  loadMessages: (params: {
    before_id?: number; after_id?: number; mode: 'initial' | 'older' | 'newer'
  }) => Promise<Message[]>
}) {
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingState) {
          const cursorId = cursorRef.current
          if (cursorId) {
            loadMessages(
              direction === 'older'
                ? { before_id: cursorId, mode: 'older' }
                : { after_id: cursorId, mode: 'newer' }
            )
          }
        }
      },
      { root: containerRef.current, threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadingState, direction, loadMessages, containerRef, sentinelRef, cursorRef])
}

/** 从 Map state setter 中移除指定 id */
function removeFromMap<K, V>(
  setter: React.Dispatch<React.SetStateAction<Map<K, V>>>,
  id: K,
) {
  setter((prev) => {
    const next = new Map(prev)
    next.delete(id)
    return next
  })
}

/** 向 Map state setter 中添加指定键值对 */
function addToMap<K, V>(
  setter: React.Dispatch<React.SetStateAction<Map<K, V>>>,
  id: K,
  value: V,
) {
  setter((prev) => {
    const next = new Map(prev)
    next.set(id, value)
    return next
  })
}

export default function ChatView({ conversationType, conversationId, myRole, overlayHost }: ChatViewProps) {
  // ── 群视界：群聊绑定的世界（全屏入口弹窗，先不加载消息） ──
  const [boundWorldId, setBoundWorldId] = useState<number | null>(null)
  const [worldModalOpen, setWorldModalOpen] = useState(false)
  const t = useT()
  const { user } = useAuth()
  const userId = user?.id
  const [messages, setMessages] = useState<Message[]>([])
  const [revokeNotice, setRevokeNotice] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [loadingState, setLoadingState] = useState<'initial' | 'older' | 'newer' | null>(null)
  const [inputHeight, setInputHeight] = useState<number | null>(null)  // 输入框高度（null=自动）
  const inputResizeRef = useRef<boolean>(false)
  const [hasMoreBefore, setHasMoreBefore] = useState(false)
  const [hasMoreAfter, setHasMoreAfter] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [firstUnreadId, setFirstUnreadId] = useState<number | null>(null)
  const [showJumpToUnread, setShowJumpToUnread] = useState(false)
  const [profileCard, setProfileCard] = useState<{
    type: string; id: number; name: string; state?: string
  } | null>(null)
  const [thinkingAgents, setThinkingAgents] = useState<Map<number, { name: string; avatarUrl: string | null }>>(new Map())
  const [typingAgents, setTypingAgents] = useState<Map<number, { name: string; avatarUrl: string | null }>>(new Map())
  // 人类打字状态: userId → {name, avatarUrl}
  const [humanTyping, setHumanTyping] = useState<Map<number, { name: string; avatarUrl: string | null }>>(new Map())

  // 合并所有活动用户（AI 思考 + AI 输入中 + 人类打字）
  const activityUsers: ActivityUser[] = useMemo(() => {
    const map = new Map<number, ActivityUser>()

    thinkingAgents.forEach((info, id) => {
      map.set(id, { id, name: info.name, avatarUrl: info.avatarUrl, status: 'thinking' })
    })
    typingAgents.forEach((info, id) => {
      const existing = map.get(id)
      if (existing) existing.status = 'typing'
      else map.set(id, { id, name: info.name, avatarUrl: info.avatarUrl, status: 'typing' })
    })
    humanTyping.forEach(({ name, avatarUrl }, id) => {
      map.set(id, { id, name, avatarUrl, status: 'typing' })
    })

    return Array.from(map.values())
  }, [thinkingAgents, typingAgents, humanTyping])

  // @提及 自动补全（仅群聊）
  const [groupMembers, setGroupMembers] = useState<Array<{ type: string; id: number; name: string; state?: string }>>([])
  const [mentionActive, setMentionActive] = useState(false)
  const [replyTo, setReplyTo] = useState<{ id: number; sender_name: string; content: string } | null>(null)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIdx, setMentionIdx] = useState(0)

  // 文件附件：上传逻辑与群视界世界对话共用 useAttachmentUpload
  //（图片如何进 LLM 多模态由后端 app/utils/multimodal.py 统一处理）
  const attachments = useAttachmentUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const inputRef = useRef(input)
  inputRef.current = input // 保持同步，供 effect cleanup 闭包读取最新值
  const containerRef = useRef<HTMLDivElement>(null)

  // ── 虚拟列表状态（窗口化渲染，长对话不卡）──
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(600)
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const bottomSentinelRef = useRef<HTMLDivElement>(null)
  const firstUnreadRef = useRef<HTMLDivElement>(null)
  const prevMessageCount = useRef(0)
  const isAutoScrolling = useRef(false)
  const prevScrollHeight = useRef(0)
  const isAtBottomRef = useRef(true)
  // 贴底状态只有这一个写入口：state 给渲染用，ref 给 rAF 节流之外要同步读的地方用
  //（滚动监听、ws 回调）。两处各写各的会打架——「看着在底部」和「其实翻走了」同时成立
  const setAtBottom = useCallback((value: boolean) => {
    isAtBottomRef.current = value
    setIsAtBottom(value)
  }, [])
  const newestIdRef = useRef<number | null>(null)       // 离开时保存已读位置
  const oldestIdRef = useRef<number | null>(null)        // 供哨兵读取，避免 messages 依赖
  const typingRef = useRef(false)                       // 当前是否正在输入中，避免重复发送 typing 消息
  const showJumpToUnreadRef = useRef(false)              // 避免 scroll 中高频 setState

  // 离开对话时保存已读位置（通过 newestIdRef，无需额外的 messages 同步 effect）
  useEffect(() => {
    return () => {
      if (newestIdRef.current && conversationId) {
        const key = `lastRead_${conversationType}_${conversationId}`
        localStorage.setItem(key, String(newestIdRef.current))
      }
    }
  }, [conversationType, conversationId])

  // 输入框草稿缓存：切换对话时保存旧草稿、恢复新草稿；页面刷新/崩溃后内容不丢
  useEffect(() => {
    if (!conversationId) return
    const draftKey = `draft_${conversationType}_${conversationId}`
    const draft = localStorage.getItem(draftKey)
    setInput(draft || '')
    return () => {
      if (inputRef.current.trim()) {
        localStorage.setItem(draftKey, inputRef.current)
      } else {
        localStorage.removeItem(draftKey)
      }
    }
  }, [conversationType, conversationId])

  // 输入中自动保存草稿（500ms 防抖，防止崩溃/掉线丢失）
  useEffect(() => {
    if (!conversationId) return
    const draftKey = `draft_${conversationType}_${conversationId}`
    const timer = setTimeout(() => {
      if (input.trim()) {
        localStorage.setItem(draftKey, input)
      } else {
        localStorage.removeItem(draftKey)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [input, conversationType, conversationId])

  // 页面刷新/关闭时同步保存草稿（beforeunload 是同步事件，cleanup 不保证执行）
  useEffect(() => {
    if (!conversationId) return
    const draftKey = `draft_${conversationType}_${conversationId}`
    const handleBeforeUnload = () => {
      if (inputRef.current?.trim()) {
        localStorage.setItem(draftKey, inputRef.current)
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [conversationType, conversationId])

  // 移动端键盘弹出时滚动到输入框位置（visualViewport API，比 setTimeout 更精确）
  useEffect(() => {
    if (window.innerWidth >= 768 || !window.visualViewport) return
    return onKeyboardChange((visible) => {
      if (visible && textareaRef.current) {
        // 键盘弹出 → 等 visualViewport 重排完成后滚动到输入框
        requestAnimationFrame(() => {
          textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
        })
      }
    })
  }, [])

  // 短延时任务是"这一屏的收尾动作"（滚到底、打字状态回退）：切对话时必须一起清掉，
  // 否则 50ms 后那个滚动会把刚进去的新对话也拽到底部，本该停在首个未读处
  const pendingTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const schedule = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(() => {
      pendingTimers.current.delete(id)
      fn()
    }, ms)
    pendingTimers.current.add(id)
  }, [])

  // 程序化滚动期间滚动监听要闭嘴（否则会被当成"用户滚走了"）
  const markAutoScrolling = useCallback((ms = 500) => {
    isAutoScrolling.current = true
    schedule(() => { isAutoScrolling.current = false }, ms)
  }, [schedule])

  useEffect(() => {
    const timers = pendingTimers.current
    return () => {
      timers.forEach(clearTimeout)
      timers.clear()
    }
  }, [conversationType, conversationId])

  const handleMessage = useCallback((msg: WebSocketMessage) => {
    if (msg.type === 'message') {
      const m = msg.data
      // 过滤：仅处理当前对话的消息（防止 DM 邀请卡闪现到群聊）
      if (!isMessageForThisConversation(m, conversationType, conversationId)) return
      setMessages((prev) => {
        // 去重：防止 WebSocket 与 HTTP fetch 竞态导致同 ID 消息重复
        if (prev.some((existing) => existing.id === m.id)) return prev
        const next = [...prev, m]
        newestIdRef.current = m.id
        return next
      })
      if (isAtBottomRef.current) {
        schedule(() => scrollToBottom(true), 50)
      }
      // 不做任何列表广播：能收到**所有**会话推送的是常驻通知连接，会话列表由它就地改
      //（见 hooks/useChatLists）。在这里广播一次 = 三个消费者各拉一次列表，白费
      if (m.sender_type === 'ai' && m.sender_id) {
        removeFromMap(setThinkingAgents, m.sender_id)
        removeFromMap(setTypingAgents, m.sender_id)
      }
    } else if (msg.type === 'message_revoked') {
      const d = msg.data
      if (!isMessageForThisConversation(d, conversationType, conversationId)) return
      setMessages((prev) => prev.map((m) => (m.id === d.id ? { ...m, revoked: true, content: '' } : m)))
    } else if (msg.type === 'ai_thinking') {
      const d = msg.data
      if (d.trigger === 'auto') return
      if (!isMessageForThisConversation(d, conversationType, conversationId)) return
      addToMap(setThinkingAgents, d.user_id, { name: d.agent_name, avatarUrl: d.agent_avatar_url || null })
    } else if (msg.type === 'ai_thinking_end') {
      const d = msg.data
      if (d.trigger === 'auto') return
      if (!isMessageForThisConversation(d, conversationType, conversationId)) return
      removeFromMap(setThinkingAgents, d.user_id)
      removeFromMap(setTypingAgents, d.user_id)
    } else if (msg.type === 'ai_typing') {
      const d = msg.data
      if (d.trigger === 'auto') return
      if (!isMessageForThisConversation(d, conversationType, conversationId)) return
      schedule(() => removeFromMap(setThinkingAgents, d.user_id), 1500)
      addToMap(setTypingAgents, d.user_id, { name: d.agent_name, avatarUrl: d.agent_avatar_url || null })
    } else if (msg.type === 'typing') {
      // 人类打字状态
      const d = msg.data
      if (!isMessageForThisConversation(d, conversationType, conversationId)) return
      if (d.is_typing) {
        setHumanTyping(prev => {
          const next = new Map(prev)
          next.set(d.sender_id, { name: d.username, avatarUrl: d.avatar_url || null })
          return next
        })
      } else {
        setHumanTyping(prev => {
          const next = new Map(prev)
          next.delete(d.sender_id)
          return next
        })
      }
    } else if (msg.type === 'announcement') {
      const d = msg.data
      if (d.group_id !== conversationId) return
      setMessages((prev) => [...prev, {
        id: -Date.now(),
        sender_type: 'system',
        sender_id: 0,
        sender_name: t('groupSettings:announcement'),
        content: d.content,
        reply_to: null,
        created_at: new Date().toISOString(),
      } as Message])
    } else if (msg.type === 'avatar_updated') {
      const d = msg as any
      const etype = d.entity_type
      const eid = d.entity_id
      const newUrl = d.avatar_url
      if (etype && eid && newUrl) {
        setMessages((prev) =>
          prev.map((m) =>
            m.sender_type === etype && m.sender_id === eid
              ? { ...m, sender_avatar_url: newUrl }
              : m,
          ),
        )
      }
    } else if (msg.type === 'user_online' || msg.type === 'user_offline') {
      // 在线状态变化 → 通知 ChatArea 更新在线人数
      window.dispatchEvent(new CustomEvent('online-count-change', { detail: msg }))
    } else if (msg.type === 'state_change') {
      // DM 对方状态变化 → 通知 DMChatView 更新头部状态点
      window.dispatchEvent(new CustomEvent('dm-partner-state-change', { detail: msg.data }))
    } else if (msg.type === 'dm_notification' || msg.type === 'unread_update') {
      window.dispatchEvent(new CustomEvent(CHAT_REFRESH_EVENT, { detail: msg }))
    } else if (msg.type === 'maintenance_update') {
      // 全局广播：维护模式状态变化
      window.dispatchEvent(new CustomEvent('ws-maintenance-update', { detail: msg }))
    }
  }, [conversationType, conversationId, t, schedule])

  const { connected, reconnecting, errors, sendMessage, sendTyping, clearErrors } = useWebSocket(
    conversationType, conversationId, { onMessage: handleMessage },
  )

  // 获取上传大小限制（缓存 5 分钟）
  useEffect(() => {
    const cached = sessionStorage.getItem('upload_limits')
    if (cached) {
      const parsed = JSON.parse(cached)
      if (Date.now() - parsed.ts < 300000) return // 缓存有效
    }
    api.get('/user/config/upload-limits').then((limits: any) => {
      sessionStorage.setItem('upload_limits', JSON.stringify({ ...limits, ts: Date.now() }))
    }).catch(() => {})
  }, [])

  // 切换对话 / 卸载时：清除输入中状态，避免对方看到残留的 typing 指示
  useEffect(() => {
    return () => {
      if (typingRef.current) {
        typingRef.current = false
        sendTyping(false)
      }
    }
  }, [conversationType, conversationId, sendTyping])

  // 稳定引用，配合 MessageBubble 的 React.memo 避免输入时重渲染消息列表
  const handleAvatarClick = useCallback((type: string, id: number, name: string, state?: string) => {
    setProfileCard({ type, id, name, state })
  }, [])

  // 消息列表 memo：窗口化渲染（虚拟列表）——只渲染视口附近的消息，
  // 上下用估算高度占位，解决“对话一多就卡”的 DOM 节点爆炸问题
  const heights = useMemo(() => messages.map(m => estimateMessageHeight(m)), [messages])
  const cumHeights = useMemo(() => {
    const a: number[] = [0]
    for (const h of heights) a.push(a[a.length - 1] + h)
    return a
  }, [heights])
  const totalHeight = cumHeights[cumHeights.length - 1] || 0

  const windowRange = useMemo(() => {
    if (messages.length === 0) return { s: 0, e: 0 }
    const startPx = Math.max(0, scrollTop - OVERSCAN_PX)
    const endPx = scrollTop + viewportH + OVERSCAN_PX
    let s = 0
    while (s < messages.length && cumHeights[s + 1] <= startPx) s++
    let e = s
    while (e < messages.length && cumHeights[e] < endPx) e++
    e = Math.min(messages.length, e + VIRTUAL_OVERSCAN)
    s = Math.max(0, s - VIRTUAL_OVERSCAN)
    return { s, e }
  }, [scrollTop, viewportH, messages.length, cumHeights])

  // @令牌 <@!id> 要显示成名字：群聊有成员表，私信没有（也就没有 @）
  const mentionNames = useMemo(
    () => Object.fromEntries(groupMembers.map((m) => [m.id, m.name])) as MentionNames,
    [groupMembers]
  )

  // 撤回按钮只在窗口内出现（后端仍会二次校验——这里只是别让人点了必然失败）
  const isGroupAdmin = myRole === 'owner' || myRole === 'admin'
  // 是不是我发的：必须是稳定引用——每渲染新建一个函数会让 messageElements 的 memo 每次都失效；
  // 也必须跟着登录用户走，否则换账号后撤回判定还按旧 user.id
  const isOwnMessage = useCallback(
    (msg: Message) => msg.sender_type === 'human' && msg.sender_id === userId,
    [userId],
  )
  const canRevoke = useCallback(
    (msg: Message) =>
      !msg.revoked
      && (isOwnMessage(msg) || isGroupAdmin)   // 群主/管理员能撤别人的（口径与后端 is_group_admin 一致）
      // 必须按 UTC 解析：后端 DateTime 是 naive UTC，直接 new Date(...) 会当成本地时间
      //（UTC+8 下每条消息都被算成"过了 8 小时"，窗口判定永远不成立——2026-09-26 的教训）
      && Date.now() - parseServerDate(msg.created_at).getTime() < REVOKE_WINDOW_MS,
    [isGroupAdmin, isOwnMessage],
  )

  /** 撤回：服务端说了算（2 分钟内、只能撤自己的）；成功那条会经 WS 广播变成占位 */
  const handleRevoke = useCallback(async (messageId: number) => {
    const base = conversationType === 'dm' ? `/dm/${conversationId}` : `/gm/${conversationId}`
    try {
      const res: any = await api.post(`${base}/messages/${messageId}/revoke`)
      const failed = (res?.channel || []).filter((c: any) => !c.ok)
      if (failed.length) {
        // 通道侧没撤掉要如实说：QQ 只有 2 分钟窗口、还要求机器人有权限（站内已撤，别让人以为全撤了）
        setRevokeNotice(t('chat:revokedChannelFailed', {
          reason: failed.map((c: any) => c.reason).join('；'),
        }))
      }
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, revoked: true, content: '' } : m)))
    } catch (e: any) {
      setRevokeNotice(e?.message || String(e))
    }
  }, [conversationType, conversationId, t])

  // 按 id 直取：窗口里每条都从头 find 一遍是 O(窗口 × 全量)，而每次滚动都会重算一遍
  const messageById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages])

  const messageElements = useMemo(() => {
    const { s, e } = windowRange
    const items = messages.slice(s, e).map((msg) => {
      const replyToData = msg.reply_to == null ? undefined : (() => {
        const quoted = messageById.get(msg.reply_to)
        if (!quoted) return { id: msg.reply_to, sender: '?', content: '' }
        return { id: quoted.id, sender: quoted.sender_name || `用户${quoted.sender_id}`, content: renderMentions(quoted.content.slice(0, 80), mentionNames) }
      })()
      return (
        <div
          key={msg.id}
          data-message-id={msg.id}
          ref={msg.id === firstUnreadId ? firstUnreadRef : undefined}
        >
          {msg.id === firstUnreadId && hasMoreBefore && (
            <div className="flex items-center gap-2 my-3 select-none">
              <div className="flex-1 h-px bg-rose-500/30" />
              <span className="text-3xs font-medium text-rose-400 whitespace-nowrap">{t('chat:newMessages')}</span>
              <div className="flex-1 h-px bg-rose-500/30" />
            </div>
          )}
          <MessageBubble
            senderName={msg.sender_name || `${msg.sender_type}:${msg.sender_id}`}
            senderAvatarUrl={msg.sender_avatar_url}
            content={renderMentions(msg.content, mentionNames)}
            isMine={isOwnMessage(msg)}
            revoked={msg.revoked}
            onRevoke={canRevoke(msg) ? handleRevoke : undefined}
            createdAt={msg.created_at}
            senderType={msg.sender_type}
            senderId={msg.sender_id}
            state={msg.sender_state ?? undefined}
            sourcePublicId={msg.source_public_id}
            via={msg.via}
            attachments={msg.attachments}
            messageType={msg.message_type}
            messageId={msg.id}
            replyTo={replyToData}
            onAvatarClick={handleAvatarClick}
            onReply={(id, name, text) => setReplyTo({ id, sender_name: name, content: text.slice(0, 80) })}
          />
        </div>
      )
    })
    return {
      beforeH: cumHeights[s],
      afterH: totalHeight - cumHeights[e],
      items,
    }
  }, [windowRange, messages, messageById, cumHeights, totalHeight, firstUnreadId, hasMoreBefore, isOwnMessage, handleAvatarClick, mentionNames, canRevoke, handleRevoke, t])

  // ============================================================
  // 消息加载器
  // ============================================================

  const loadMessages = useCallback(async (params: {
    before_id?: number
    after_id?: number
    mode: 'initial' | 'older' | 'newer'
  }) => {
    const { before_id, after_id, mode } = params
    // 避免 initial 模式重复设值（调用方已设过）
    if (mode !== 'initial') setLoadingState(mode)

    // 加载旧消息前记录当前滚动高度
    if (mode === 'older' && containerRef.current) {
      prevScrollHeight.current = containerRef.current.scrollHeight
    }

    try {
      const queryParams = new URLSearchParams()
      queryParams.set('limit', String(PAGE_SIZE))
      if (before_id) queryParams.set('before_id', String(before_id))
      if (after_id) queryParams.set('after_id', String(after_id))

      let fetched: Message[]
      if (conversationType === 'group') {
        fetched = await api.get<Message[]>(
          `/gm/${conversationId}/messages?${queryParams.toString()}`
        )
      } else {
        fetched = await api.get<Message[]>(
          `/dm/${conversationId}/messages?${queryParams.toString()}`
        )
      }

      const gotFullPage = fetched.length >= PAGE_SIZE

      // 更新 refs（避免 IntersectionObserver 依赖 messages 数组）
      if (fetched.length > 0) {
        newestIdRef.current = fetched[fetched.length - 1].id
        oldestIdRef.current = fetched[0].id
      }

      setMessages((prev) => {
        if (mode === 'initial') return fetched
        // 按 id 归并：两个方向都往同一份列表里插，谁在前谁在后由 id 说，不靠拼接顺序。
        // 「往前插 / 往后接」只在列表本身没有洞时成立；补丢消息补的正是洞，那时拼接会把
        // 新页塞到列表末尾（2026-10-04 群 69 那屏：10-03 的消息下面直接接 10-04 深夜的）
        const byId = new Map(prev.map((m) => [m.id, m]))
        for (const m of fetched) byId.set(m.id, m)
        return [...byId.values()].sort((a, b) => a.id - b.id)
      })

      if (mode === 'older') {
        setHasMoreBefore(gotFullPage)
        if (!gotFullPage) setFirstUnreadId(null)
        // 恢复滚动位置：旧消息插入头部后，补偿新增高度
        requestAnimationFrame(() => {
          if (containerRef.current && prevScrollHeight.current > 0) {
            const newHeight = containerRef.current.scrollHeight
            containerRef.current.scrollTop += (newHeight - prevScrollHeight.current)
            prevScrollHeight.current = 0
          }
        })
      } else if (mode === 'newer') {
        setHasMoreAfter(gotFullPage)
      } else {
        // initial
        setHasMoreBefore(gotFullPage)
        setHasMoreAfter(false)

        if (fetched.length > 0) {
          const key = `lastRead_${conversationType}_${conversationId}`
          const stored = localStorage.getItem(key)
          const lastReadId = stored ? parseInt(stored) : null

          if (lastReadId && lastReadId > 0) {
            // 上次已读过的对话：找第一条 > lastReadId 的消息作为"首个未读"
            const firstUnread = fetched.find(m => m.id > lastReadId)
            if (firstUnread) {
              setFirstUnreadId(firstUnread.id)
            }
            // 所有消息都已读 → firstUnreadId 保持 null
          } else if (gotFullPage) {
            // 首次访问且有更多历史消息 → 最旧那条作为未读边界
            setFirstUnreadId(fetched[0].id)
          }
          // 首次访问且消息 ≤ PAGE_SIZE → 全部可见，无需未读标记
        }
      }
      return fetched
    } catch (err) {
      console.error('加载消息失败:', err)
      return [] as Message[]
    } finally {
      setLoadingState(null)
    }
  }, [conversationId, conversationType])

  // 补齐游标之后丢掉的那段消息
  // 断线/漏推期间缺的那一截：分页只会往前翻，够不着中间（观感："16 分钟前上面直接是 2 天前"）。
  // 从游标处一页页往后拉、按 id 归并，直到不足一页。
  // 返回补齐段的第一条 id（红线画它上面，不是当页最旧那条）；没补到就是 null
  const catchUpFrom = useCallback(async (fromId: number | null): Promise<number | null> => {
    if (!fromId) return null
    let cursor = fromId
    let boundary: number | null = null
    for (let i = 0; i < CATCH_UP_MAX_PAGES; i++) {
      const page = await loadMessages({ after_id: cursor, mode: 'newer' })
      if (page.length > 0 && boundary === null) boundary = page[0].id
      if (page.length < PAGE_SIZE) return boundary
      const last = page[page.length - 1]?.id
      if (!last || last === cursor) return boundary
      cursor = last
    }
    return boundary
  }, [loadMessages])

  // ============================================================
  // 滚动辅助
  // ============================================================

  const scrollToBottom = useCallback((smooth = true) => {
    const el = containerRef.current
    if (!el) return
    markAutoScrolling()
    // 程序化滚动期间滚动监听闭嘴（见 markAutoScrolling），"到没到底"得自己说。
    // 不说的话：点完"回到底部"状态还停在"翻着往上看"，未读就清不掉
    setAtBottom(true)
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'instant' })
  }, [markAutoScrolling, setAtBottom])

  const scrollToMessage = useCallback((messageId: number) => {
    const el = containerRef.current
    if (!el) return
    const idx = messages.findIndex(m => m.id === messageId)
    if (idx >= 0 && cumHeights[idx] !== undefined) {
      // 目标可能在渲染窗口外：先滚到估算位置，窗口更新后再精确定位
      el.scrollTop = cumHeights[idx]
    }
    requestAnimationFrame(() => {
      const msgEl = el.querySelector(`[data-message-id="${messageId}"]`)
      if (msgEl) {
        markAutoScrolling()
        // 只滚动消息容器本身（scrollIntoView 会连带滚动外层 main/Layout，把标题栏滚出视口）
        scrollToInContainer(el, msgEl as HTMLElement, { smooth: true })
      }
    })
  }, [messages, cumHeights, markAutoScrolling])

  const handleJumpToUnread = useCallback(async () => {
    if (!firstUnreadId) return
    // 加载 firstUnreadId 之前的一页消息
    await loadMessages({ before_id: firstUnreadId, mode: 'older' })
    // 滚动到原来的 firstUnreadId 位置。走 schedule：200ms 内切了对话，这次定位
    // 拿的是旧对话的 id 与高度表，作用到新对话上就是把人家滚到随机位置
    schedule(() => scrollToMessage(firstUnreadId), 200)
  }, [firstUnreadId, loadMessages, scrollToMessage, schedule])

  // ============================================================
  // 初始加载
  // ============================================================

  const loadInitialMessages = useCallback(async () => {
    if (!conversationId) return
    if (conversationType === 'group') {
      const membersData = await api.get(`/groups/${conversationId}/members`)
      setGroupMembers(membersData)
    }
    await loadMessages({ mode: 'initial' })
    // 上次已读位置落在最新那一页之前（离开超过一页的量）时，未读的主体正好夹在中间：
    // 只加载最新一页的话，红线只能画在当页最旧那条上，中间那批也翻不到
    const stored = localStorage.getItem(`lastRead_${conversationType}_${conversationId}`)
    const lastReadId = stored ? parseInt(stored) : null
    const newest = newestIdRef.current
    if (!lastReadId || !newest || lastReadId >= newest) return
    const boundary = await catchUpFrom(lastReadId)
    if (boundary) setFirstUnreadId(boundary)
  }, [conversationType, conversationId, loadMessages, catchUpFrom])

  // Tauri 那条"3 秒窗口没开成 → 整页兜底跳转"的待定标记：用户改选标准界面、切走对话后作废
  const immersivePending = useRef(false)

  // 全屏弹窗：选择「在此标准界面打开」→ 关弹窗 + 加载消息
  const closeWorldModal = useCallback(() => {
    immersivePending.current = false
    setWorldModalOpen(false)
    loadInitialMessages()
  }, [loadInitialMessages])

  // 沉浸界面：独立窗口打开（按世界复用）——桌面 Tauri 用 WebviewWindow，网页用 window.open 新窗口
  // 失败/被拦截 → 回退应用内全屏
  const openImmersive = useCallback(() => {
    if (boundWorldId == null) return
    const url = `/world-view/${boundWorldId}?group_id=${conversationId}`
    if ('__TAURI_INTERNALS__' in window) {
      ;(async () => {
        try {
          const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow')
          const label = `world-immersive-${boundWorldId}`
          const existing = await WebviewWindow.getByLabel(label)
          if (existing) { existing.setFocus(); return }
          const win = new WebviewWindow(label, { url })
          immersivePending.current = true
          // 兜底跳转同样算"这一屏的收尾动作"：3 秒内切了对话，或用户改点「在此标准界面打开」，
          // 这次整页跳转已经不是用户要的了；created 事件也会把待定标记作废
          schedule(() => {
            if (immersivePending.current) window.location.href = url
          }, 3000)
          win.once('tauri://created', () => { immersivePending.current = false })
          win.once('tauri://error', () => { immersivePending.current = false; window.location.href = url })
        } catch {
          window.location.href = url  // 无权限/失败 → 回退应用内
        }
      })()
      return
    }
    // 网页端：命名窗口（同名复用+聚焦）；WebView/被弹窗拦截（返回 null）→ 回退应用内
    if (!tryOpenWorldWindow(boundWorldId, typeof conversationId === 'number' ? conversationId : undefined)) window.location.href = url
  }, [boundWorldId, conversationId, schedule])

  useEffect(() => {
    if (!conversationId) return
    setThinkingAgents(new Map())
    setTypingAgents(new Map())
    setMessages([])
    setHasMoreBefore(false)
    setHasMoreAfter(false)
    setFirstUnreadId(null)
    setShowJumpToUnread(false)
    setAtBottom(true)
    prevMessageCount.current = 0
    // 上一屏没走完的收尾动作不能带进新对话：pendingTimers 清了定时器，但"正在自动滚动"这个
    // 布尔标记得在这里归零——滚动监听看到它为真就整段 return，留着会让新对话的底部状态不再更新
    isAutoScrolling.current = false
    prevScrollHeight.current = 0
    immersivePending.current = false
    // 世界门属于「当前这个群」：上一屏的绑定状态与弹窗不带进新对话，
    // 否则切到未绑定世界的群，遮罩还盖着、按钮指向的还是上一个世界
    setBoundWorldId(null)
    setWorldModalOpen(false)
    setLoadingState('initial')
    // 进入对话时查询当前 AI 思考/输入中状态（组件重建后恢复活动指示器）
    const activityUrl = conversationType === 'group'
      ? `/groups/${conversationId}/activity`
      : `/dm/${conversationId}/activity`
    api.get<Record<string, {name: string; avatar_url: string | null}>>(activityUrl).then(active => {
      const thinking = new Map<number, {name: string; avatarUrl: string | null}>()
      Object.entries(active).forEach(([id, info]) => {
        thinking.set(Number(id), { name: info.name, avatarUrl: info.avatar_url })
      })
      if (thinking.size > 0) setThinkingAgents(thinking)
    }).catch(() => {})

    let cancelled = false
    const init = async () => {
      try {
        // 群视界门：群聊绑定世界 → 全屏弹窗（先不加载消息），选「标准界面」再加载
        if (conversationType === 'group') {
          try {
            const wr = await api.get<{ world_id: number }>(`/worlds/by-entity?entity_type=group&entity_id=${conversationId}`)
            if (wr.world_id) {
              if (cancelled) return
              setBoundWorldId(wr.world_id)
              setWorldModalOpen(true)
              return
            }
          } catch { /* 未绑定世界 */ }
          if (cancelled) return
        }
        await loadInitialMessages()
      } catch (err) {
        console.error('初始化失败:', err)
      }
    }
    init()
    return () => { cancelled = true }
  }, [conversationId, conversationType, loadInitialMessages, setAtBottom])

  // 初始加载后定位 + 立即保存已读位置（不等卸载，防止刷新时红线残留）
  useLayoutEffect(() => {
    if (loadingState === null && messages.length > 0 && prevMessageCount.current === 0) {
      prevMessageCount.current = messages.length
      // 立即保存已读位置：初始加载完成即视为用户已看到最新消息
      if (newestIdRef.current && conversationId) {
        const key = `lastRead_${conversationType}_${conversationId}`
        localStorage.setItem(key, String(newestIdRef.current))
      }
      const container = containerRef.current
      if (!container) return
      if (firstUnreadId) {
        // 有更早的未读消息 → 将首个未读定位到视口顶部（paint 之前，无闪烁）
        const msgEl = container.querySelector(`[data-message-id="${firstUnreadId}"]`)
        if (msgEl) {
          markAutoScrolling()
          // 只滚动消息容器本身，避免 scrollIntoView 连带滚动外层 main/Layout
          scrollToInContainer(container, msgEl as HTMLElement)
        }
      } else if (container.scrollHeight > container.clientHeight) {
        // 无旧消息但内容溢出 → 滚到底部
        container.scrollTop = container.scrollHeight
      }
    }
  }, [loadingState, messages.length, firstUnreadId, markAutoScrolling])

  // 上下哨兵：加载更早/更新的分页（游标在 ref 里，见 useSentinel 的注释）
  useSentinel({
    containerRef, sentinelRef: topSentinelRef, cursorRef: oldestIdRef,
    hasMore: hasMoreBefore, direction: 'older', loadingState, loadMessages,
  })
  useSentinel({
    containerRef, sentinelRef: bottomSentinelRef, cursorRef: newestIdRef,
    hasMore: hasMoreAfter, direction: 'newer', loadingState, loadMessages,
  })

  // 断线重连：把掉线期间漏掉的消息补上（WS 只推「此刻之后」的，掉线那一段没人补，
  // 不补的话列表就永久断成两截）。首次连接不算重连——那条路由初始加载负责。
  const wasConnectedRef = useRef(false)
  useEffect(() => {
    if (!connected) {
      wasConnectedRef.current = false
      return
    }
    if (wasConnectedRef.current) catchUpFrom(newestIdRef.current)
    wasConnectedRef.current = true
  }, [connected, catchUpFrom])

  // ============================================================
  // 滚动监听：isAtBottom + showJumpToUnread（使用 rAF 节流 DOM 查询）
  // ============================================================

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let rafId = 0
    const handleScroll = () => {
      if (isAutoScrolling.current) return
      if (rafId) return // 上一帧的 DOM 查询尚未执行，跳过
      rafId = requestAnimationFrame(() => {
        rafId = 0
        const { scrollTop: st, scrollHeight, clientHeight } = container
        // 虚拟列表：跟踪滚动位置与视口高度（值变化才触发渲染）
        setViewportH(clientHeight)
        setScrollTop(prev => (Math.abs(prev - st) > 1 ? st : prev))
        const atBottom = scrollHeight - st - clientHeight < BOTTOM_THRESHOLD
        setAtBottom(atBottom)

        if (firstUnreadId) {
          const msgEl = container.querySelector(`[data-message-id="${firstUnreadId}"]`)
          const visible = msgEl
            ? msgEl.getBoundingClientRect().bottom >= container.getBoundingClientRect().top
            : false
          // 仅值变化时才触发渲染
          if (visible !== showJumpToUnreadRef.current) {
            showJumpToUnreadRef.current = !visible
            setShowJumpToUnread(!visible)
          }
        }
      })
    }
    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [firstUnreadId])

  // ============================================================
  // 未读：贴底 = 正在读
  // ============================================================

  // 上报「我贴在这个会话底部」：侧边栏据此不画红数泡——是判定，不是等后端回传再抹掉
  useEffect(() => {
    if (!conversationId || !isAtBottom) {
      setFollowingConversation(null)
      return
    }
    setFollowingConversation(conversationKey(conversationType, conversationId))
    return () => setFollowingConversation(null)
  }, [conversationType, conversationId, isAtBottom])

  const reading = useReadingKey() === conversationKey(conversationType, conversationId)

  const syncRead = useCallback(() => {
    if (!conversationId) return
    const url = conversationType === 'group'
      ? `/groups/${conversationId}/read`
      : `/dm/${conversationId}/read`
    api.post(url)
      // 落库成功才清本地那一项：反过来的话，读失败时侧栏会假装读过了
      .then(() => markConversationRead(conversationType, conversationId))
      .catch(() => {})
  }, [conversationType, conversationId])

  // 正在读，就把这一屏的已读落回后端。触发它的四件事——进会话（贴底初始为真）、
  // 滚回底部、收到新消息、标签页切回前台——都落在 reading 或「最新一条」的变化上
  const newestId = messages.length > 0 ? messages[messages.length - 1].id : null
  useEffect(() => {
    if (!reading) return
    syncRead()
  }, [reading, newestId, syncRead])

  // ============================================================
  // Mermaid 错误报告
  // ============================================================
  // 用 ref 存 handleSend，避免每次渲染重建事件监听
  const handleSendRef = useRef<typeof handleSend>(null as any)
  // handleSendRef.current 在后面 handleSend 定义后赋值
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.message) {
        handleSendRef.current(detail.message)
      }
    }
    document.addEventListener('mermaid-error-report', handler)
    return () => document.removeEventListener('mermaid-error-report', handler)
  }, [])

  // ============================================================
  // @提及逻辑（仅群聊）
  // ============================================================

  const mentionFiltered = useMemo(() => mentionQuery
    ? groupMembers.filter((m) => m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
    : groupMembers
  , [mentionQuery, groupMembers])

  const detectMention = (value: string, cursorPos: number) => {
    if (conversationType === 'dm') return
    const beforeCursor = value.slice(0, cursorPos)
    const atIdx = beforeCursor.lastIndexOf('@')
    if (atIdx === -1) { setMentionActive(false); return }
    const prev = atIdx > 0 ? beforeCursor[atIdx - 1] : ''
    const allowed = prev === '' || prev === ' ' || /[，。！？、；：""【】（）]/.test(prev)
    if (!allowed) { setMentionActive(false); return }
    const query = beforeCursor.slice(atIdx + 1, cursorPos)
    if (query.includes(' ')) { setMentionActive(false); return }
    setMentionQuery(query)
    setMentionIdx(0)
    setMentionActive(true)
  }

  const insertMention = (name: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const value = input
    const cursorPos = ta.selectionStart
    const beforeCursor = value.slice(0, cursorPos)
    const atIdx = beforeCursor.lastIndexOf('@')
    if (atIdx === -1) return
    const newBefore = beforeCursor.slice(0, atIdx) + '@' + name + ' '
    const newValue = newBefore + value.slice(cursorPos)
    setInput(newValue)
    setMentionActive(false)
    requestAnimationFrame(() => {
      ta.focus()
      const newPos = newBefore.length
      ta.setSelectionRange(newPos, newPos)
    })
  }

  // 文件上传处理（超限/失败记成错误态附件，不再 alert 打断流程）
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) attachments.pick(e.target.files)
    // 清空 input 以便重复选择同一文件
    e.target.value = ''
  }


  const handleSend = (text: string) => {
    if (!text.trim() && attachments.items.length === 0) return
    if (!conversationId) return
    if (!connected) return

    // 还有上传中 / 上传失败的文件 → 不发（hook 已把状态标在附件条上）
    if (attachments.uploading || attachments.hasError) return

    const readyAttachments = attachments.ready
    sendMessage(text.trim(), replyTo?.id, readyAttachments.length > 0 ? readyAttachments : undefined)
    setInput('')
    setReplyTo(null)
    // 发送后清除输入中状态
    if (typingRef.current) {
      typingRef.current = false
      sendTyping(false)
    }
    localStorage.removeItem(`draft_${conversationType}_${conversationId}`)
    attachments.clear()
    setMentionActive(false)
    window.dispatchEvent(new CustomEvent(CHAT_REFRESH_EVENT, { detail: { type: 'message_sent' } }))
  }
  // 故意在 render 里赋值（不放进 effect）：mermaid 错误事件可能在本轮 effect 跑完前就到，
  // 走 effect 同步会在那之前拿到 null
  handleSendRef.current = handleSend

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionActive && mentionFiltered.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((prev) => (prev + 1) % mentionFiltered.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((prev) => (prev - 1 + mentionFiltered.length) % mentionFiltered.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionFiltered[mentionIdx].name); return }
      if (e.key === 'Escape') { e.preventDefault(); setMentionActive(false); return }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); }
  }

  // ============================================================
  // 渲染
  // ============================================================

  // 世界门是「群聊面板」级浮层：本组件里的 absolute 只到消息区，面板顶部标题栏会露在遮罩外面，
  // 所以整块交给 ChatArea 的面板容器渲染；宿主还没就绪时就地渲染，避免门没出现而消息也不加载
  const worldGate = worldModalOpen && boundWorldId != null && conversationType === 'group' ? (
    <div className="absolute inset-0 z-toast flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 max-h-full overflow-y-auto bg-surface rounded-dialog border border-primary-500/30 shadow-2xl p-8 text-center">
        <div className="mb-4"><Globe size={48} className="mx-auto text-primary-400" /></div>
        <h2 className="text-lg font-semibold text-textPrimary">{t('chat:worldGateTitle')}</h2>
        <p className="text-sm text-textMuted mt-2 mb-7">{t('chat:worldGateSubtitle')}</p>
        <div className="space-y-2.5">
          <button onClick={openImmersive} className="btn btn-md btn-primary w-full gap-1.5">
            <Gamepad2 size={14} /> {t('chat:worldGateImmersive')}
          </button>
          <button
            onClick={closeWorldModal}
            className="btn btn-md btn-outline w-full"
          >
            <Settings size={12} /> {t('chat:worldGateStandard')}
          </button>
        </div>
      </div>
    </div>
  ) : null

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
      {/* 重连提示条 */}
      {reconnecting && (
        <div className="absolute top-0 left-0 right-0 z-modal flex items-center justify-center gap-2 bg-accent-500/15 border-b border-accent-500/20 text-accent-400 px-4 py-1.5 text-xs font-medium backdrop-blur-sm">
          <Loader2 size={12} className="animate-spin" />
          {t('chat:reconnecting')}
        </div>
      )}

      {/* 沉浸入口：从世界门选了标准界面之后，进群这一次就再也找不到入口，所以顶部常驻一条 */}
      {conversationType === 'group' && boundWorldId != null && !worldModalOpen && (
        <button
          onClick={openImmersive}
          className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-1.5 text-xs font-medium text-primary-400 bg-primary-500/10 border-b border-primary-500/20 hover:bg-primary-500/15 transition-colors"
        >
          <Gamepad2 size={12} /> {t('chat:worldEnterImmersive')}
        </button>
      )}

      {/* 隐藏的文件输入（附件按钮触发） */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* 错误 Toast */}
      {errors.length > 0 && (
        <div className="absolute top-4 right-4 z-modal space-y-1 max-w-sm">
          {errors.map((err) => (
            <div
              key={err.timestamp}
              className="flex items-start gap-2 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-card px-3 py-2 text-sm shadow-lg shadow-black/20"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span className="flex-1">{err.message}</span>
              <button onClick={clearErrors} className="shrink-0 text-rose-400/60 hover:text-rose-400">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 撤回提示：通道侧没撤掉这类"半成功"必须让人看见，不能假装全撤了 */}
      {revokeNotice && (
        <div className="absolute top-4 right-4 z-modal max-w-sm">
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-500 rounded-card px-3 py-2 text-sm shadow-lg shadow-black/20">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span className="flex-1">{revokeNotice}</span>
            <button onClick={() => setRevokeNotice(null)} className="shrink-0 text-amber-500/60 hover:text-amber-500">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ↑ 跳至首个未读消息 */}
      {showJumpToUnread && firstUnreadId && (
        <button
          onClick={handleJumpToUnread}
          className="absolute top-3 right-4 z-drawer flex items-center gap-1.5 px-3 py-1.5 bg-primary-500/90 hover:bg-primary-500 text-white text-xs font-medium rounded-full shadow-lg shadow-primary-500/30 backdrop-blur-sm transition-all duration-200"
        >
          <ArrowUp size={14} />
          {t('chat:jumpToFirstUnread')}
        </button>
      )}

      {/* 群视界全屏入口：渲染进群聊面板（见 worldGate），先弹窗、不加载消息；选「标准界面」才关并加载 */}
      {worldGate && (overlayHost ? createPortal(worldGate, overlayHost) : worldGate)}

      {/* 消息列表：外层不滚动，专门用来挂拖拽蒙版（放进滚动容器会随内容滚走）；
          containerRef 必须留在内层——虚拟列表靠它读 scrollTop */}
      <div className="flex-1 min-h-0 relative" {...attachments.zoneProps('list')}>
        <DropMask {...attachments.dropState('list')} label={t('chat:dropToAdd')} />
        <div
          ref={containerRef}
          className="absolute inset-0 overflow-y-auto px-2 py-3 md:px-4 md:py-4 bg-canvas"
        >
          {/* 顶部哨兵（加载更旧消息的触发器） */}
          <div ref={topSentinelRef} className="h-1" />

          {/* 顶部加载指示器 */}
          {loadingState === 'older' && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="animate-spin text-textMuted" size={16} />
            </div>
          )}

          {/* 无更多旧消息提示 */}
          {!hasMoreBefore && messages.length > 0 && (
            <div className="text-center text-3xs text-textMuted py-2 select-none">
              {t('chat:beginningOfChat')}
            </div>
          )}

          {/* 初始加载 */}
          {loadingState === 'initial' ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-textMuted" size={24} />
            </div>
          ) : messages.length === 0 ? (
            <EmptyState icon={MessageSquare} title={conversationType === 'dm' ? '开始私信' : '开始群聊'} description={conversationType === 'dm' ? '给对方发送第一条消息吧' : '在群里发送第一条消息吧'} />
          ) : (
            <div style={{ paddingTop: messageElements.beforeH, paddingBottom: messageElements.afterH }}>
              {messageElements.items}
            </div>
          )}

          {/* 底部活动状态栏：合并 AI 思考/输入 + 人类打字 */}
          <ActivityBar users={activityUsers} />

          {/* 底部加载指示器 */}
          {loadingState === 'newer' && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="animate-spin text-textMuted" size={16} />
            </div>
          )}

          {/* 底部哨兵（加载更新消息的触发器） */}
          <div ref={bottomSentinelRef} className="h-1" />
        </div>
      </div>

      {/* ↓ 回到底部浮动按钮 */}
      {!isAtBottom && messages.length > 0 && (
        <div className="absolute bottom-20 right-6 z-drawer">
          <button
            onClick={() => scrollToBottom(true)}
            className="flex items-center justify-center w-9 h-9 bg-elevated border border-border rounded-full shadow-lg shadow-black/20 text-textSecondary hover:text-textPrimary hover:bg-surface transition-all duration-200"
            title={t('chat:scrollToBottom')}
          >
            <ArrowDown size={16} />
          </button>
        </div>
      )}

      {/* 输入框拖拽调整手柄 */}
      <div
        className="relative h-1.5 cursor-row-resize hover:bg-primary-400/30 active:bg-primary-400/50 transition-colors shrink-0"
        onMouseDown={(e) => {
          inputResizeRef.current = true
          const startY = e.clientY
          // 从当前视觉高度起拖：inputHeight 存的就是总高的下限，自动增长那部分已经在里面
          const startTotalH = Math.max(CHAT_INPUT_MIN_H, textareaRef.current?.offsetHeight || 0)
          const onMove = (ev: MouseEvent) => {
            if (!inputResizeRef.current) return
            const deltaY = startY - ev.clientY  // 向上拖=正值=扩大
            setInputHeight(Math.max(CHAT_INPUT_MIN_H, startTotalH + deltaY))
          }
          const onUp = () => {
            inputResizeRef.current = false
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
          }
          window.addEventListener('mousemove', onMove)
          window.addEventListener('mouseup', onUp)
        }}
      />

      {/* 输入框（文件可直接拖进来放下，与点回形针等价） */}
      <div className="p-3 bg-surface border-t border-border relative" {...attachments.zoneProps('input')} {...attachments.pasteProps}>
        <DropMask {...attachments.dropState('input')} label={t('chat:dropToAdd')} />
        {/* 附件预览列表（与群视界世界对话共用同一组件） */}
        <AttachmentChips items={attachments.items} onRemove={attachments.remove} errorText={t('chat:uploadFailed')} />

        {/* @提及 自动补全下拉 */}
        {mentionActive && mentionFiltered.length > 0 && (
          <MenuPanel className="absolute bottom-full left-3 right-3 mb-1 max-h-48 overflow-y-auto z-modal">
            {mentionFiltered.map((m, i) => (
              <MenuItem
                key={`${m.type}:${m.id}`}
                active={i === mentionIdx}
                onClick={() => insertMention(m.name)}
                className="flex items-center gap-2 py-2 text-sm"
              >
                <span className={`w-2 h-2 rounded-full shrink-0 ${getStateDotColor(m.state)}`} />
                <span className="font-medium">{m.name}</span>
                <span className="text-textMuted text-xs ml-auto">
                  {m.type === 'ai' ? <><Bot size={12} className="inline" /> AI</> : <User size={12} className="inline" />}
                </span>
              </MenuItem>
            ))}
          </MenuPanel>
        )}

        {replyTo && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-t-xl border border-border bg-surface/50 text-xs">
            <div className="w-0.5 h-8 bg-primary-400 rounded-full shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-primary-400 font-medium truncate">回复 @{replyTo.sender_name}</div>
              <div className="text-textMuted truncate"><EmojiText content={replyTo.content} /></div>
            </div>
            <button onClick={() => setReplyTo(null)} className="shrink-0 p-1 rounded-control hover:bg-elevated text-textMuted hover:text-textPrimary transition-colors">
              <X size={14} />
            </button>
          </div>
        )}
        <ChatInput
          ref={textareaRef}
          conversationType={conversationType}
          conversationId={conversationId}
          t={t}
          onSend={handleSend}
          connected={connected}
          onSendFile={() => fileInputRef.current?.click()}
          hasAttachments={attachments.items.length > 0}
          groupMembers={groupMembers}
          inputHeight={inputHeight}
        />
      </div>

      {/* 资料卡 */}
      {profileCard && (
        <ProfileCard
          entityType={profileCard.type as 'human' | 'ai'}
          entityId={profileCard.id}
          entityName={profileCard.name}
          state={profileCard.state}
          onClose={() => setProfileCard(null)}
        />
      )}
    </div>
  )
}
